import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { formatDateTimeVE } from '../utils/formatters';

const formatDateTime = (iso) => formatDateTimeVE(iso, { second: '2-digit' });

// ─── Camera Modal ─────────────────────────────────────────────────────────────
function CameraModal({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError('No se pudo acceder a la cámara. Verifica los permisos del navegador.'));
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCaptured(canvas.toDataURL('image/jpeg', 0.85));
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const retake = () => {
    setCaptured(null);
    setError('');
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError('No se pudo acceder a la cámara.'));
  };

  const confirm = () => {
    if (!captured) return;
    fetch(captured).then((r) => r.blob()).then((blob) => onCapture(blob, captured));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex justify-between items-center px-5 py-4 border-b">
          <h3 className="font-semibold text-gray-800 text-lg">Tomar Foto</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-3">{error}</div>}
          {!captured ? (
            <>
              <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg bg-black" style={{ maxHeight: '320px', objectFit: 'cover' }} />
              <canvas ref={canvasRef} className="hidden" />
              <button onClick={takePhoto} disabled={!!error} className="mt-4 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-lg transition">
                📸 Capturar
              </button>
            </>
          ) : (
            <>
              <img src={captured} alt="preview" className="w-full rounded-lg" style={{ maxHeight: '320px', objectFit: 'cover' }} />
              <div className="mt-4 flex gap-3">
                <button onClick={retake} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg transition">Retomar</button>
                <button onClick={confirm} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition">Confirmar</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Photo Viewer Modal ────────────────────────────────────────────────────────
function PhotoModal({ record, onClose }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record) return;
    setSrc(null);
    setError(false);
    setLoading(true);
    apiClient
      .get(`/attendance/photo/${record.id}`, { responseType: 'blob' })
      .then((res) => { setSrc(URL.createObjectURL(res.data)); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [record]);

  const retry = () => {
    setSrc(null);
    setError(false);
    setLoading(true);
    apiClient
      .get(`/attendance/photo/${record.id}`, { responseType: 'blob' })
      .then((res) => { setSrc(URL.createObjectURL(res.data)); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  };

  if (!record) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <div>
            <p className="font-semibold text-gray-800">{record.user_name}</p>
            <p className="text-xs text-gray-500">
              {record.type === 'check_in' ? 'Entrada' : 'Salida'} — {record.store_name} — {formatDateTime(record.timestamp)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-4 flex flex-col items-center justify-center bg-gray-100 min-h-48">
          {loading && <p className="text-gray-500 py-12">Cargando foto…</p>}
          {error && !loading && (
            <div className="text-center py-8">
              <p className="text-red-500 text-sm mb-3">No se pudo cargar la foto</p>
              <button onClick={retry} className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-2 rounded-lg">
                Reintentar
              </button>
            </div>
          )}
          {src && !loading && (
            <img src={src} alt="Foto de asistencia" className="max-h-96 rounded-lg object-contain" />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Mark Attendance Panel ─────────────────────────────────────────────────────
function MarkPanel({ type, label, color, stores, onSuccess }) {
  const [step, setStep] = useState('idle');
  const [storeId, setStoreId] = useState('');
  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');

  const handleCapture = (blob, preview) => {
    setPhotoBlob(blob);
    setPhotoPreview(preview);
    setStep('preview');
  };

  const submit = async () => {
    setError('');
    setStep('submitting');
    try {
      const form = new FormData();
      form.append('type', type);
      form.append('store_id', storeId);
      form.append('photo', photoBlob, 'attendance.jpg');
      await apiClient.post('/attendance/mark', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStep('idle');
      setStoreId('');
      setPhotoBlob(null);
      setPhotoPreview(null);
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar el registro');
      setStep('preview');
    }
  };

  const reset = () => { setStep('idle'); setStoreId(''); setPhotoBlob(null); setPhotoPreview(null); setError(''); };

  const bgClass = color === 'green' ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200';
  const btnClass = color === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600';

  return (
    <div className={`border-2 rounded-2xl p-6 ${bgClass} flex flex-col gap-4`}>
      <h2 className="text-xl font-bold text-gray-800 text-center">{label}</h2>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

      {step === 'idle' && (
        <button onClick={() => setStep('select-store')} className={`${btnClass} text-white font-bold py-4 rounded-xl text-lg transition`}>
          {label}
        </button>
      )}

      {step === 'select-store' && (
        <div className="flex flex-col gap-3">
          <label className="text-sm font-medium text-gray-700">¿En qué sede estás?</label>
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Selecciona una sede…</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="flex gap-2">
            <button onClick={reset} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg text-sm transition">Cancelar</button>
            <button onClick={() => setStep('camera')} disabled={!storeId} className={`flex-1 ${btnClass} disabled:bg-gray-300 text-white py-2 rounded-lg text-sm font-semibold transition`}>
              Siguiente → Foto
            </button>
          </div>
        </div>
      )}

      {step === 'camera' && <CameraModal onCapture={handleCapture} onClose={() => setStep('select-store')} />}

      {step === 'preview' && (
        <div className="flex flex-col gap-3">
          <img src={photoPreview} alt="preview" className="rounded-lg w-full object-cover" style={{ maxHeight: '200px' }} />
          <p className="text-xs text-gray-500 text-center">Sede: <strong>{stores.find((s) => s.id === storeId)?.name}</strong></p>
          <div className="flex gap-2">
            <button onClick={() => setStep('camera')} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg text-sm transition">Retomar</button>
            <button onClick={submit} className={`flex-1 ${btnClass} text-white py-2 rounded-lg text-sm font-semibold transition`}>Confirmar y Guardar</button>
          </div>
        </div>
      )}

      {step === 'submitting' && <div className="text-center py-4 text-gray-600">Guardando…</div>}
    </div>
  );
}

// ─── Attendance Tab ────────────────────────────────────────────────────────────
function AttendanceTab({ stores, isAdmin }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [filterUserId, setFilterUserId] = useState('');

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom + 'T00:00:00';
      if (dateTo) params.date_to = dateTo + 'T23:59:59';
      if (filterUserId) params.user_id = filterUserId;
      const res = await apiClient.get('/attendance/history', { params });
      setRecords(res.data);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterUserId]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const uniqueUsers = [...new Map(records.map((r) => [r.user_id, { id: r.user_id, name: r.user_name }])).values()];

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <MarkPanel type="check_in" label="Marcar Entrada" color="green" stores={stores} onSuccess={loadRecords} />
        <MarkPanel type="check_out" label="Marcar Salida" color="orange" stores={stores} onSuccess={loadRecords} />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Historial</h2>
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Desde</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Hasta</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          {isAdmin && uniqueUsers.length > 0 && (
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">Empleado</label>
              <select value={filterUserId} onChange={(e) => setFilterUserId(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                <option value="">Todos</option>
                {uniqueUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-end">
            <button onClick={loadRecords} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition">Buscar</button>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-8">Cargando…</p>
        ) : records.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No hay registros para el período seleccionado</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 font-semibold text-gray-600 rounded-tl-lg">Tipo</th>
                  {isAdmin && <th className="px-3 py-2 font-semibold text-gray-600">Empleado</th>}
                  <th className="px-3 py-2 font-semibold text-gray-600">Sede</th>
                  <th className="px-3 py-2 font-semibold text-gray-600">Fecha y Hora</th>
                  <th className="px-3 py-2 font-semibold text-gray-600 rounded-tr-lg">Foto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${r.type === 'check_in' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {r.type === 'check_in' ? '→ Entrada' : '← Salida'}
                      </span>
                    </td>
                    {isAdmin && <td className="px-3 py-2 text-gray-700">{r.user_name}</td>}
                    <td className="px-3 py-2 text-gray-700">{r.store_name}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                    <td className="px-3 py-2">
                      {r.photo_id
                        ? <button onClick={() => setViewingPhoto(r)} className="text-blue-600 hover:text-blue-800 text-xs underline">Ver foto</button>
                        : <span className="text-gray-300 text-xs">Sin foto</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewingPhoto && <PhotoModal record={viewingPhoto} onClose={() => setViewingPhoto(null)} />}
    </>
  );
}

// ─── Employees Tab ─────────────────────────────────────────────────────────────
function EmployeesTab({ stores }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/attendance/employees');
      setEmployees(res.data);
    } catch {
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !storeId) return;
    setError('');
    setSaving(true);
    try {
      await apiClient.post('/attendance/employees', { first_name: firstName.trim(), last_name: lastName.trim(), store_id: storeId });
      setFirstName('');
      setLastName('');
      setStoreId('');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este empleado del registro de asistencia?')) return;
    try {
      await apiClient.delete(`/attendance/employees/${id}`);
      setEmployees((prev) => prev.filter((e) => e.id !== id));
    } catch {
      alert('Error al eliminar');
    }
  };

  return (
    <div className="max-w-2xl">
      {/* Form */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Registrar Empleado</h2>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4">{error}</div>}
        <form onSubmit={handleAdd} className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nombre</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Nombre"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Apellido</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Apellido"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sede</label>
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecciona una sede…</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-2 rounded-lg text-sm transition"
          >
            {saving ? 'Guardando…' : 'Agregar Empleado'}
          </button>
        </form>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Empleados Registrados</h2>
        {loading ? (
          <p className="text-center text-gray-400 py-6">Cargando…</p>
        ) : employees.length === 0 ? (
          <p className="text-center text-gray-400 py-6">No hay empleados registrados aún</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-3 py-2 font-semibold text-gray-600 rounded-tl-lg">Nombre</th>
                <th className="px-3 py-2 font-semibold text-gray-600">Sede</th>
                <th className="px-3 py-2 font-semibold text-gray-600 rounded-tr-lg"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {employees.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-800">{emp.first_name} {emp.last_name}</td>
                  <td className="px-3 py-2 text-gray-500">{emp.store_name}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleDelete(emp.id)}
                      className="text-red-500 hover:text-red-700 text-xs font-medium"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function AttendanceControl() {
  const { isAdmin, user } = useAuth();
  const [stores, setStores] = useState([]);
  const [activeTab, setActiveTab] = useState('attendance');

  const canManageEmployees = isAdmin || user?.role === 'compras';

  useEffect(() => {
    apiClient.get('/stores')
      .then((res) => setStores(res.data.filter((s) => s.is_active !== false)))
      .catch(() => setStores([]));
  }, []);

  const tabs = [
    { id: 'attendance', label: 'Asistencia' },
    ...(canManageEmployees ? [{ id: 'employees', label: 'Empleados' }] : []),
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Control de Asistencia</h1>
      <p className="text-sm text-gray-500 mb-6">Registra tu entrada y salida con foto de verificación</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2 text-sm font-medium rounded-t-lg transition ${
              activeTab === tab.id
                ? 'bg-white border border-b-white border-gray-200 text-blue-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'attendance' && <AttendanceTab stores={stores} isAdmin={isAdmin} />}
      {activeTab === 'employees' && canManageEmployees && <EmployeesTab stores={stores} />}
    </div>
  );
}
